import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Check, Lock, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { LEVEL_STYLES, LevelGem } from "@/components/CreatorLevelBadge";
import { CountUp } from "@/components/CountUp";
import { GemZoom } from "@/components/GemZoom";
import { GemCinematic } from "@/components/GemCinematic";
import { buildLevelShareCard } from "@/components/levelShareCard";
import { fxGemSpin } from "@/utils/gamefx";
import StoryStudio from "@/components/story/StoryStudio";
import "./levelVault.css";

const DEFAULT_LADDER = [
  { level: 1, name: "Smeraldo", min_points: 0 },
  { level: 2, name: "Zaffiro", min_points: 1000 },
  { level: 3, name: "Rubino", min_points: 5000 },
  { level: 4, name: "Diamante", min_points: 20000 },
  { level: 5, name: "Diamante Nero", min_points: 75000 },
  { level: 6, name: "Red Diamond", min_points: 250000 },
];

const NF = new Intl.NumberFormat("it-IT", { useGrouping: "always" });
const fmt = (n) => NF.format(n || 0);

/** Vetrina Livelli — solo sul proprio profilo. Diamante Nero e Red Diamond restano bloccati. */
export const LevelVault = ({ open, onClose, isCreator }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareFile, setShareFile] = useState(null);

  useEffect(() => {
    if (!open || !isCreator) return;
    setFailed(false);
    api.get("/creators/level")
      .then((r) => setData(r.data))
      .catch(() => setFailed(true));
  }, [open, isCreator]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const ready = !!data;
  const ladder = data?.levels?.length ? data.levels : DEFAULT_LADDER;
  const current = isCreator && ready ? (data.creator_level || 1) : 1;
  const points = isCreator ? (data?.creator_level_points || 0) : 0;
  const cur = LEVEL_STYLES[current] || LEVEL_STYLES[1];
  const next = data?.next_level || null;
  const nextStyle = next ? (LEVEL_STYLES[next.level] || LEVEL_STYLES[2]) : null;
  const progress = data?.progress_percent || 0;

  const openZoom = () => {
    if (!isCreator || !ready) return;
    fxGemSpin();
    setZoom(true);
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const file = await buildLevelShareCard({
        level: current,
        name: data?.level_name || cur.name,
        points,
        ladder,
      });
      setShareFile(file);
    } catch {
      toast.error("Non riesco a creare l'immagine. Riprova.");
    } finally {
      setSharing(false);
    }
  };

  return createPortal(
    <div className="lvv-root" data-testid="level-vault" onClick={onClose}>
      <motion.div
        className="lvv-sheet"
        style={{ "--lv-ring": cur.ring }}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lvv-grab" />
        <div className="lvv-head">
          <span className="lvv-title">Vetrina Livelli</span>
          <button className="lvv-close" onClick={onClose} data-testid="level-vault-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="lvv-hero">
          <span className="lvv-hero-aura"
            style={{ background: `radial-gradient(circle, ${cur.ring}4d, transparent 66%)` }} />
          <span className="lvv-rays" />
          <button className="lvv-gem-btn" onClick={openZoom} data-testid="level-vault-gem"
            aria-label="Ingrandisci gemma">
            {isCreator
              ? <GemCinematic level={current} size={150} />
              : <LevelGem level={current} size={150} spin locked />}
          </button>
          <p className="lvv-name" data-testid="level-vault-name"
            style={{ textShadow: `0 0 42px ${cur.ring}99` }}>
            {isCreator ? (data?.level_name || cur.name) : "Bloccato"}
          </p>
          <p className="lvv-sub">{isCreator ? `Livello ${current} di ${ladder.length}` : "Programma Creator"}</p>

          {isCreator && ready && (
            <>
              <p className="lvv-points" data-testid="level-vault-points"
                style={{ textShadow: `0 0 30px ${cur.ring}80` }}>
                <CountUp value={points} />
              </p>
              <p className="lvv-points-lab">Punti Creator</p>
              <button className="lvv-share" onClick={handleShare} disabled={sharing}
                data-testid="level-vault-share-btn">
                <Share2 className="w-3.5 h-3.5" />
                {sharing ? "Creo l'immagine..." : "Condividi nelle storie"}
              </button>
            </>
          )}
        </div>

        {isCreator && failed ? (
          <div className="lvv-next">
            <p className="lvv-next-val" style={{ textAlign: "center" }} data-testid="level-vault-error">
              Non riesco a caricare i tuoi livelli. Riprova tra poco.
            </p>
          </div>
        ) : !isCreator ? (
          <>
            <p className="lvv-locked-note">
              I livelli si sbloccano con i Punti Creator che guadagni dai regali ricevuti.
              Attiva il Programma Creator per iniziare la scalata.
            </p>
            <button className="lvv-cta" data-testid="level-vault-become-creator"
              onClick={() => { onClose?.(); navigate("/app/creator-apply"); }}>
              <Sparkles className="w-4 h-4 inline mr-2 align-[-3px]" /> Diventa Creator
            </button>
          </>
        ) : next ? (
          <div className="lvv-next" style={{ "--lv-next": nextStyle.ring }} data-testid="level-vault-next">
            <div className="lvv-next-top">
              <LevelGem level={next.level} size={44} className="lvv-pulse"
                locked={!!LEVEL_STYLES[next.level]?.soon} />
              <div className="lvv-next-txt">
                <p className="lvv-next-lab">Prossimo traguardo</p>
                <p className="lvv-next-val">
                  {LEVEL_STYLES[next.level]?.soon && !data?.points_to_next_level
                    ? <>{next.name} sarà sbloccato prossimamente</>
                    : <>Ti mancano <b>{fmt(data?.points_to_next_level)}</b> punti a {next.name}</>}
                </p>
              </div>
            </div>
            <div className="lvv-bar">
              <motion.span
                initial={{ width: 0 }} animate={{ width: `${progress}%` }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
                style={{ background: `linear-gradient(90deg, ${cur.ring}, ${nextStyle.ring})` }}
              />
            </div>
            <div className="lvv-bar-pct">
              <span>{cur.name}</span>
              <span data-testid="level-vault-progress">{Math.round(progress)}%</span>
              <span>{next.name}</span>
            </div>
          </div>
        ) : (
          <div className="lvv-next">
            <p className="lvv-next-val" style={{ textAlign: "center" }}>Hai raggiunto il livello massimo</p>
          </div>
        )}

        <p className="lvv-ladder-lab">La scalata</p>
        <div className="lvv-ladder" data-testid="level-vault-ladder">
          {ladder.map((lv) => {
            const st = LEVEL_STYLES[lv.level] || LEVEL_STYLES[1];
            const soon = !!st.soon;
            const state = !isCreator ? "locked"
              : lv.level === current ? "current"
              : lv.level < current ? "done"
              : "locked";
            return (
              <motion.div
                key={lv.level} className="lvv-step" data-state={state}
                style={{ "--lv-ring": st.ring }}
                data-testid={`level-step-${lv.level}`}
                initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + lv.level * 0.06, duration: 0.45 }}
              >
                <LevelGem level={lv.level} size={38} locked={state === "locked"} />
                <div className="lvv-step-txt">
                  <p className="lvv-step-name">{lv.name}</p>
                  <p className="lvv-step-req">
                    {soon ? "In arrivo" : `${fmt(lv.min_points)} punti`}
                  </p>
                </div>
                {state === "done" && <span className="lvv-step-tag"><Check className="w-3 h-3 inline align-[-2px]" /> Raggiunto</span>}
                {state === "current" && <span className="lvv-step-tag">Attuale</span>}
                {state === "locked" && <span className="lvv-step-tag"><Lock className="w-3 h-3 inline align-[-2px]" /> {soon ? "Presto" : "Bloccato"}</span>}
              </motion.div>
            );
          })}
        </div>

        {isCreator && (
          <button className="lvv-ghost" data-testid="level-vault-progress-link"
            onClick={() => { onClose?.(); navigate("/app/creator-progress"); }}>
            Vedi progressi completi
          </button>
        )}
      </motion.div>

      {zoom && (
        <GemZoom level={current} name={data?.level_name || cur.name} onClose={() => setZoom(false)} />
      )}

      {shareFile && (
        <StoryStudio
          file={shareFile}
          kind="image"
          onClose={() => setShareFile(null)}
          onPublished={() => {
            setShareFile(null);
            toast.success("Vetrina pubblicata nelle storie");
          }}
        />
      )}
    </div>,
    document.body
  );
};

export default LevelVault;
